<script setup>
import { computed, ref, watch } from 'vue';
import {
  parse,
  isValid,
  isAfter,
  isBefore,
  format,
  setHours,
  setMinutes,
} from 'date-fns';
import {
  getIntlDateFormatForLocale,
  CALENDAR_TYPES,
} from '../helpers/DatePickerHelper';

const props = defineProps({
  calendarType: {
    type: String,
    default: '',
  },
  dateValue: Date,
  compareDate: Date,
  isDisabled: Boolean,
  withTime: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'validate', 'error']);

const { START_CALENDAR, END_CALENDAR } = CALENDAR_TYPES;

const dateFormat = computed(() => getIntlDateFormatForLocale()?.toUpperCase());

const localDateValue = computed({
  get: () => props.dateValue?.toLocaleDateString(navigator.language) || '',
  set: newValue => {
    const localeFormat = getIntlDateFormatForLocale();
    const parsedDate = parse(newValue, localeFormat, new Date());
    if (!isValid(parsedDate)) return;

    if (props.withTime && isValid(props.dateValue)) {
      emit(
        'update',
        setMinutes(
          setHours(parsedDate, props.dateValue.getHours()),
          props.dateValue.getMinutes()
        )
      );
      return;
    }
    emit('update', parsedDate);
  },
});

const localTimeValue = ref('00:00');

watch(
  () => props.dateValue,
  value => {
    if (isValid(value)) {
      localTimeValue.value = format(value, 'HH:mm');
    } else if (props.calendarType === END_CALENDAR) {
      localTimeValue.value = '23:59';
    } else {
      localTimeValue.value = '00:00';
    }
  },
  { immediate: true }
);

const onTimeInput = event => {
  const next = event?.target?.value || '00:00';
  localTimeValue.value = next;
  const [hours, minutes] = next.split(':').map(part => Number(part));
  const base = isValid(props.dateValue) ? props.dateValue : new Date();
  emit('update', setMinutes(setHours(base, hours || 0), minutes || 0));
};

const validateDate = () => {
  if (!isValid(props.dateValue)) {
    emit('error', `Please enter the date in valid format: ${dateFormat.value}`);
    return;
  }

  const { calendarType, compareDate, dateValue } = props;
  const isStartCalendar = calendarType === START_CALENDAR;
  const isEndCalendar = calendarType === END_CALENDAR;

  if (compareDate && isStartCalendar && isAfter(dateValue, compareDate)) {
    emit('error', 'Start date must be before the end date.');
  } else if (compareDate && isEndCalendar && isBefore(dateValue, compareDate)) {
    emit('error', 'End date must be after the start date.');
  } else {
    emit('validate', dateValue);
  }
};
</script>

<template>
  <div
    class="flex flex-col items-start px-5 gap-1.5 pt-4 w-full"
    :class="withTime ? 'h-[110px]' : 'h-[82px]'"
  >
    <span class="text-sm font-medium text-n-slate-12">
      {{
        calendarType === START_CALENDAR
          ? $t('DATE_PICKER.DATE_RANGE_INPUT.START')
          : $t('DATE_PICKER.DATE_RANGE_INPUT.END')
      }}
    </span>
    <div class="flex w-full items-center gap-2">
      <input
        v-model="localDateValue"
        type="text"
        class="!text-sm !mb-0 disabled:!outline-n-strong min-w-0 flex-1"
        :placeholder="dateFormat"
        :disabled="isDisabled"
        @keypress.enter="validateDate"
      />
      <input
        v-if="withTime"
        :value="localTimeValue"
        type="time"
        class="!text-sm !mb-0 w-[7.5rem] shrink-0 disabled:!outline-n-strong"
        :disabled="false"
        @input="onTimeInput"
        @change="validateDate"
      />
    </div>
  </div>
</template>
